import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { format } from 'date-fns';

const QuestionMarkIcon = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c0-1.108.806-2.008 1.801-2.008s1.801.9 1.801 2.008c0 .621-.275 1.18-.725 1.565-.45.386-.1075.735-.1075 1.435v.435M12 19.5h.008v.008H12v-.008z" />
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" fill="none"/>
  </svg>
);

interface GanttChartProps {
  objectives: {
    id: string;
    name: string;
    fullName: string;
    deadline: string;
    displayDate: string;
    weeksRemaining: number;
    inlineFine: string;
    tooltip: string;
    startDate?: string;
    endDate?: string;
  }[];
  selectedObjectives: string[];
  onObjectiveToggle: (objectiveId: string) => void;
}

const GanttChart: React.FC<GanttChartProps> = ({ objectives, selectedObjectives, onObjectiveToggle }) => {
  if (!objectives || objectives.length === 0) return null;

  const maxWeeks = Math.max(...objectives.map(obj => Math.max(0, obj.weeksRemaining || 0)), 0);
  const ganttWidth = Math.max(400, maxWeeks * 20); // 20px per week

  const generateTimeScale = () => {
    const today = new Date();
    const monthLabels = [];
    const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    for (let i = 0; i <= Math.ceil(maxWeeks / 4); i++) {
      const month = new Date(currentMonth);
      month.setMonth(currentMonth.getMonth() + i);
      monthLabels.push({
        label: format(month, 'MMM'),
        position: (i * 4 * 20) // 4 weeks per month
      });
    }

    return monthLabels;
  };

  const timeScale = generateTimeScale();

  return (
    <div className="mb-5">
      <h2 className="h3 text-primary mb-4">Regulatory Timeline</h2>
      <div className="bg-white rounded p-4 shadow">
        <div className="table-responsive">
          <div style={{ minWidth: `${ganttWidth + 200}px` }}>
            <svg width="100%" height={objectives.length * 40 + 60}>
              {/* Time scale */}
              {timeScale.map((month, i) => (
                <g key={i}>
                  <line 
                    x1={100 + month.position} 
                    y1="20" 
                    x2={100 + month.position} 
                    y2={objectives.length * 40 + 30} 
                    stroke="#E5E7EB" 
                    strokeWidth="1"
                  />
                  <text 
                    x={100 + month.position} 
                    y="15" 
                    className="small text-muted"
                    textAnchor="middle"
                  >
                    {month.label}
                  </text>
                </g>
              ))}

              {/* Today line */}
              <line x1="100" y1="20" x2="100" y2={objectives.length * 40 + 30} stroke="#666" strokeDasharray="4 2"/>
              <text x="105" y="15" className="small text-muted">Today</text>

              {/* Objectives bars */}
              {objectives.map((obj, i) => {
                const barWidth = Math.max(20, obj.weeksRemaining * 20);
                const isSelected = selectedObjectives.includes(obj.id);
                const y = 40 + i * 40;

                const tooltip = (
                  <Tooltip id={`tooltip-${obj.id}`}>
                    <div>
                      <h6 className="fw-bold text-primary">{obj.fullName}</h6>
                      <p className="mb-1"><strong>Deadline:</strong> {obj.displayDate}</p>
                      <p className="mb-1"><strong>Penalties:</strong> {obj.tooltip}</p>
                      <p className="mb-0"><strong>Time remaining:</strong> {obj.weeksRemaining} weeks</p>
                    </div>
                  </Tooltip>
                );

                return (
                  <g key={obj.id}>
                    <text x="20" y={y + 10} className="small fw-bold text-primary">
                      {obj.name}
                    </text>
                    
                    <OverlayTrigger placement="top" overlay={tooltip}>
                      <rect
                        x={100}
                        y={y - 10}
                        width={barWidth}
                        height="20"
                        rx="4"
                        fill={isSelected ? "#14B8A6" : "#9CA3AF"}
                        style={{ cursor: 'pointer' }}
                        onClick={() => onObjectiveToggle(obj.id)}
                      />
                    </OverlayTrigger>

                    <text x={100 + barWidth + 10} y={y + 10} className="small text-muted">
                      {obj.displayDate}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GanttChart;